<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { primaryScheduleEventAction } from "../../_shared/_logic/header-actions"
  import {
    mdiArrowRightBold,
    mdiCloseCircleOutline,
    mdiContentSave,
    mdiDelete,
    mdiLockCheck,
    mdiPhoneOutgoing,
  } from "@mdi/js"
  import {
    SCHEDULING_ACTION_TOOLTIP_CLASS,
    schedulingActionTooltips,
  } from "../../../../../../../../../_shared/_logic/action-tooltips"

  type Props = {
    primaryOnly?: boolean
    event?: ScheduleEvent | null
    forceReadOnly?: boolean
    isAppointment?: boolean
    showRescheduleAction?: boolean
    showSaveAction?: boolean
    canMarkNoShow?: boolean
    canConfirmAppointment?: boolean
    contactOrderBusy?: boolean
    contactOrdersLoading?: boolean
    contactOrderLabel?: string
    contactOrderTooltip?: string | null
    showContactOrderAction?: boolean
    canStartContactOrder?: boolean
    saving?: boolean
    deleting?: boolean
    initiatingReschedule?: boolean
    confirming?: boolean
    canceling?: boolean
    canSubmit?: boolean
    onDelete: () => void
    onStartReschedule: () => void
    onMarkNoShow: () => void
    onConfirm: () => void
    onStartContactOrder: () => void
    onSubmit: () => void
  }

  const {
    primaryOnly = false,
    event = null,
    forceReadOnly = false,
    isAppointment = false,
    showRescheduleAction = false,
    showSaveAction = false,
    canMarkNoShow = false,
    canConfirmAppointment = false,
    contactOrderBusy = false,
    contactOrdersLoading = false,
    contactOrderLabel = "Ankündigungsplan erstellen",
    contactOrderTooltip = null,
    showContactOrderAction = true,
    canStartContactOrder = false,
    saving = false,
    deleting = false,
    initiatingReschedule = false,
    confirming = false,
    canceling = false,
    canSubmit = false,
    onDelete,
    onStartReschedule,
    onMarkNoShow,
    onConfirm,
    onStartContactOrder,
    onSubmit,
  }: Props = $props()

  const isConfirmed = $derived(event?.status === "CONFIRMED")
  const showDeleteAction = $derived(Boolean(event?.id && !forceReadOnly && !isAppointment))
  const showRunnableContactOrderAction = $derived(
    Boolean(isAppointment && showContactOrderAction && (canStartContactOrder || contactOrderBusy)),
  )
  const primaryAction = $derived(
    primaryScheduleEventAction({ showSaveAction, canConfirmAppointment, showRescheduleAction }),
  )
  const deleteLabel = $derived(isAppointment ? "Termin löschen" : "Eintrag löschen")
</script>

<div class="event-actions">
  {#if primaryAction}
    <div class="primary-action">
      {#if showSaveAction}
        <CTA
          type="primaryButton"
          content=""
          ariaLabel="Speichern"
          tooltipContent={schedulingActionTooltips.eventSave}
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          {#snippet iconSnippet()}
            <Icon path={mdiContentSave} />
          {/snippet}
        </CTA>
      {/if}
      {#if isAppointment}
        {#if canConfirmAppointment || isConfirmed}
          <CTA
            type="primaryButton"
            content={confirming ? "Bestätige…" : isConfirmed ? "Bestätigt" : "Bestätigen"}
            tooltipContent={isConfirmed
              ? "Termin ist bereits bestätigt."
              : schedulingActionTooltips.eventConfirm}
            tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
            onClick={onConfirm}
            disabled={isConfirmed || confirming || saving || deleting || canceling}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiLockCheck} />
            {/snippet}
          </CTA>
        {/if}
        {#if showRescheduleAction}
          <CTA
            type="primaryButton"
            content={initiatingReschedule ? "Starte…" : "Verschieben"}
            tooltipContent={schedulingActionTooltips.eventReschedule}
            tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
            onClick={onStartReschedule}
            disabled={saving || deleting || initiatingReschedule}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiArrowRightBold} />
            {/snippet}
          </CTA>
        {/if}
      {:else if primaryAction === "confirm"}
          <CTA
          type="primaryButton"
          content={confirming ? "Bestätige…" : "Bestätigen"}
          tooltipContent={schedulingActionTooltips.eventConfirm}
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
          onClick={onConfirm}
          disabled={confirming || saving || deleting || canceling}
        >
            {#snippet iconSnippet()}
              <Icon path={mdiLockCheck} />
            {/snippet}
          </CTA>
        {:else if primaryAction === "reschedule"}
          <CTA
          type="primaryButton"
          content={initiatingReschedule ? "Starte…" : "Verschieben"}
          tooltipContent={schedulingActionTooltips.eventReschedule}
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
          onClick={onStartReschedule}
          disabled={saving || deleting || initiatingReschedule}
        >
            {#snippet iconSnippet()}
              <Icon path={mdiArrowRightBold} />
            {/snippet}
          </CTA>
      {/if}
    </div>
  {/if}

  {#if !primaryOnly}
    <div class="secondary-actions">
      {#if showDeleteAction}
        <CTA
          type="secondaryButton"
          content={deleting ? "Lösche…" : deleteLabel}
          onClick={onDelete}
          disabled={saving || deleting}
          tooltipContent={schedulingActionTooltips.eventDelete}
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
        >
          {#snippet iconSnippet()}
            <Icon path={mdiDelete} />
          {/snippet}
        </CTA>
      {/if}
      {#if !isAppointment && showRescheduleAction && primaryAction !== "reschedule"}
        <CTA
          type="secondaryButton"
          content={initiatingReschedule ? "Starte…" : "Verschieben"}
          tooltipContent={schedulingActionTooltips.eventReschedule}
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
          onClick={onStartReschedule}
          disabled={saving || deleting || initiatingReschedule}
        >
          {#snippet iconSnippet()}
            <Icon path={mdiArrowRightBold} />
          {/snippet}
        </CTA>
      {/if}
      {#if canMarkNoShow}
        <CTA
          type="secondaryButton"
          content={canceling ? "Setze…" : "Nicht stattgefunden"}
          onClick={onMarkNoShow}
          disabled={canceling || saving || deleting || confirming}
          tooltipContent={schedulingActionTooltips.eventNoShow}
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
        >
          {#snippet iconSnippet()}
            <Icon path={mdiCloseCircleOutline} />
          {/snippet}
        </CTA>
      {/if}
      {#if showRunnableContactOrderAction}
        <CTA
          type="secondaryButton"
          content={contactOrderBusy
            ? contactOrdersLoading
              ? "Lädt…"
              : "Starte…"
            : contactOrderLabel}
          onClick={onStartContactOrder}
          disabled={!canStartContactOrder}
          tooltipContent={contactOrderTooltip ?? schedulingActionTooltips.eventContactOrder}
          tooltipClassName={SCHEDULING_ACTION_TOOLTIP_CLASS}
        >
          {#snippet iconSnippet()}
            <Icon path={mdiPhoneOutgoing} />
          {/snippet}
        </CTA>
      {/if}
    </div>
  {/if}
</div>

<style lang="less">
  .event-actions,
  .primary-action,
  .secondary-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-start;
    gap: 0.55rem;
    max-width: 100%;
  }

  .event-actions {
    width: 100%;
  }

  .secondary-actions {
    min-width: 0;
  }

  :global(.event-actions button.cta) {
    max-width: 100%;
  }

  :global(.event-actions button.cta p) {
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
